/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getId } from '@fluentui/react/lib/Utilities';
import {
    azureSearchServiceSwaggerToAzureSearchStore,
    csAccountSwaggerToCSAccountStore,
    resourceGroupSwaggerToResourceGroupStore,
    subscriptionSwaggerToSubscriptionStore,
    tenantSwaggerToTenantStore
} from 'src/core/handlers/azure/azureMappers';
import { checkForAzurePermissions } from 'src/core/handlers/azure/utils';
import { getAzureService } from 'src/core/services/azure/azureService';
import {
    AccountProvisioningStateSwagger,
    AzureCsAccountProps,
    AzureCsAccountSwagger,
    AzurePermissionsSwagger,
    AzureSkuInfo,
    OldProvisioningStateSwagger,
    ProvisioningStateSwagger
} from 'src/core/services/azure/azureSwaggers';
import { AzureCsAccountKind, AzureCsAccountStore, AzureSku } from 'src/core/stores/AzureCsAccountStore';
import { AzureSearchStore } from 'src/core/stores/AzureSearchStore';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { AzureSubscriptionStore } from 'src/core/stores/AzureSubscriptionStore';
import { RootStore } from 'src/core/stores/RootStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { Region } from 'src/core/types/regionTypes';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { batchPromises, getNextAbleResults } from 'src/core/utils/batchPromises';
import { getEventBus } from 'src/core/utils/eventBus';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';

export type AzureHandlerGroup = ReturnType<typeof createAzureHandlerGroup>;

const sortByName = <T extends { name: string }>(data: T[]) =>
    data.sort((a, b) => (a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1));

const defaultAllowableAccountKinds: AzureCsAccountKind[] = [
    'LUIS',
    'OpenAI',
    'TextAnalytics',
    'LUIS.Authoring',
    'TextTranslation',
    'CognitiveServices'
];

export const loadTenants = async (neutralToken: string, rootStore: RootStore) => {
    const azureService = getAzureService();
    const { tenantStoreHouse } = rootStore.azureStore;

    if (tenantStoreHouse.tracker.isRunningOrCompleted) {
        return;
    }

    try {
        tenantStoreHouse.tracker.setState('running');

        const chunkedTenantSwaggers = await azureService.getAzureTenants({ neutralToken });
        const tenants = sortByName(chunkedTenantSwaggers.data.map(tenantSwaggerToTenantStore));

        tenantStoreHouse.setItems(tenants);
        tenantStoreHouse.tracker.setState('completed');

        return tenants;
    } catch (e) {
        tenantStoreHouse.tracker.setState('failed', <Error>e);
        throw e;
    }
};

/**
 * Creates a handler group of functions for Azure resources to
 * be fired by the dispatcher.
 *
 * @param dependencies The dependencies required by this factory.
 */
export const createAzureHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const azureService = getAzureService();

    const getAzureTenants = async () => {
        await loadTenants(rootStore.azureStore.session.neutralToken, rootStore);
    };

    /**
     * Loads tenant specific subscription(s).
     */
    const getAzureSubscriptions = async (params?: HandlerParams) => {
        const { forceRefresh } = params?.context;
        const { subscriptionStoreHouse } = rootStore.azureStore;

        if (!forceRefresh && (subscriptionStoreHouse.tracker.isRunning || subscriptionStoreHouse.tracker.isCompleted)) {
            return;
        }

        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        subscriptionStoreHouse.tracker.setState('running');

        try {
            const chunkedAzureSubscriptionSwaggers = await azureService.getAzureSubscriptionsByTenant({ tenantToken });

            subscriptionStoreHouse.setItems(sortByName(chunkedAzureSubscriptionSwaggers.data.map(subscriptionSwaggerToSubscriptionStore)));

            subscriptionStoreHouse.tracker.setState('completed');
        } catch (e) {
            subscriptionStoreHouse.tracker.setState('failed', <Error>e);
        }
    };

    /**
     * Loads tenant specific resource group(s).
     *
     * @param param0.id The id of the subscription to get the resource
     * groups for.
     */
    const getAzureResourceGroups = async ({ id }: Pick<AzureSubscriptionStore, 'id'>) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        rootStore.azureStore.resourceGroupStoreHouse.tracker.setState('running');

        try {
            const chunkedAzureResourceGroupSwaggers = await azureService.getAzureResourceGroupsBySubscriptionId({ tenantToken, id });
            rootStore.azureStore.resourceGroupStoreHouse.setItems(
                sortByName(chunkedAzureResourceGroupSwaggers.data.map(resourceGroupSwaggerToResourceGroupStore))
            );
            rootStore.azureStore.resourceGroupStoreHouse.tracker.setState('completed');
        } catch (e) {
            rootStore.azureStore.resourceGroupStoreHouse.tracker.setState('failed', <Error>e);
            throw e;
        }
    };

    /**
     * Loads tenant specific azure cognitive service account(s) and their keys.
     *
     * @param param0.id The id of the subscription to get the accounts for.
     * @param param0.localStoreHouse The store to use to store the accounts.
     */
    const getAzureCsAccountsBySubscriptionId = async ({
        id,
        localStoreHouse,
        filterPredicate
    }: HandlerParams<
        Pick<AzureSubscriptionStore, 'id'> & {
            localStoreHouse: ResourceStoreHouse<AzureCsAccountStore>;
            filterPredicate?: (item: AzureCsAccountStore) => boolean;
        }
    >) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        localStoreHouse.tracker.setState('running');
        try {
            const chunkedCsAccountsSwagger = await azureService.getAzureCsAccountsBySubscriptionId({ tenantToken, id });
            const csAccountsStores = chunkedCsAccountsSwagger.data.map(acc => csAccountSwaggerToCSAccountStore(acc, null));

            const filteredAccountsStores = filterPredicate
                ? csAccountsStores.filter(filterPredicate)
                : csAccountsStores.filter(acc => defaultAllowableAccountKinds.some(kind => kind === acc.kind));

            localStoreHouse.setItems(sortByName(filteredAccountsStores));
            localStoreHouse.tracker.setState('completed');
        } catch (e) {
            localStoreHouse.tracker.setState('failed', <Error>e);

            throw e;
        }
    };

    /**
     * Loads tenant specific azure AML workspace(s).
     *
     * @param param0.id The id of the subscription to get the accounts for.
     * @param param0.localStoreHouse The store to use to store the accounts.
     */
    const getAzureAmlWorkspacesBySubscriptionId = async ({
        id,
        localStoreHouse
    }: HandlerParams<
        Pick<AzureSubscriptionStore, 'id'> & {
            localStoreHouse: ResourceStoreHouse<AzureCsAccountStore>;
        }
    >) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        localStoreHouse.tracker.setState('running');
        try {
            const chunkedCsAccountsSwagger = await azureService.getAzureMLWorkspacesBySubscriptionId({ tenantToken, id });
            const csAccountsStores = chunkedCsAccountsSwagger.data.map(acc => csAccountSwaggerToCSAccountStore(acc, null));

            localStoreHouse.setItems(sortByName(csAccountsStores));
            localStoreHouse.tracker.setState('completed');
        } catch (e) {
            localStoreHouse.tracker.setState('failed', <Error>e);

            throw e;
        }
    };

    /**
     * Loads tenant specific azure cognitive service account(s).
     *
     * @param param0.id The id of the subscription to get the accounts for.
     * @param param0.name The resource group name to be used to fetch accounts for.
     * @param param0.localStoreHouse The store to use to store the accounts in.
     */
    const getAzureCsAccountsByResourceGroupName = async ({
        id,
        name,
        localStoreHouse
    }: HandlerParams<{
        id: string;
        name: string;
        localStoreHouse: ResourceStoreHouse<AzureCsAccountStore>;
    }>) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        localStoreHouse.tracker.setState('running');
        try {
            const chunkedCsAccountsSwagger = await azureService.getAzureCsAccountsByResourceGroupName({ tenantToken, id, name });
            const filteredAccountsStores = chunkedCsAccountsSwagger.data
                .map(acc => csAccountSwaggerToCSAccountStore(acc, null))
                .filter(acc => defaultAllowableAccountKinds.some(kind => kind === acc.kind));

            localStoreHouse.setItems(sortByName(filteredAccountsStores));
            localStoreHouse.tracker.setState('completed');
        } catch (e) {
            localStoreHouse.tracker.setState('failed', <Error>e);
        }
    };

    /**
     * Gets a batch of Azure accounts by their paths.
     *
     * @param param0.paths An array of key paths.
     * @param param0.localStoreHouse The store to use to store the accounts in.
     */
    const getAzureCsAccountsByPaths = async ({
        mode,
        paths,
        localStoreHouse
    }: HandlerParams<
        { paths: AzureCsAccountStore['path'][] } & { localStoreHouse: ResourceStoreHouse<AzureCsAccountStore> } & {
            mode: 'add' | 'replace';
        }
    >) => {
        const accounts: AzureCsAccountStore[] = [];
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        localStoreHouse.tracker.setState('running');
        const { results } = await batchPromises(
            paths.map(path =>
                Promise.all([
                    azureService.getAzureCsAccountByPath({ tenantToken, path }),
                    azureService.getAzureCsAccountKeyStringsByPath({ tenantToken, path })
                ])
            )
        );

        // Successful requests
        results
            .filter(result => result.success)
            .forEach(({ success }) => {
                const [accountSwagger, keyStrings] = success;

                const account = csAccountSwaggerToCSAccountStore(accountSwagger);
                account.keyStrings = keyStrings;
                accounts.push(account);
            });

        // Failed requests
        results
            .filter(result => result.error)
            .forEach(({ error }) => {
                const toastId = getId();
                // TODO: pending discussion
                getEventBus().emit('addToast', { id: toastId, quiet: true, title: error.code, content: error.message });
            });

        if (mode === 'add') {
            localStoreHouse.batchAdd(accounts);
        } else if (mode === 'replace') {
            localStoreHouse.setItems(accounts);
        }

        localStoreHouse.tracker.setState('completed');
    };

    /**
     * Updates an Azure accounts by its path.
     * if the account is the current one used in the session store,
     * it'll be updated as well.
     *
     * @param param0.sku The new account sku to update.
     * @param param0.isIdentityEnabled The new account identity to update.
     */
    const updateAzureCsAccountByPath = async ({
        skuCode,
        localStore,
        isIdentityEnabled
    }: HandlerParams<{ skuCode: AzureSku['code']; isIdentityEnabled: boolean; localStore: AzureCsAccountStore }>) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        const dataToUpdate = <Pick<AzureCsAccountSwagger, 'identity' | 'sku'>>{
            sku: { name: skuCode },
            identity: { type: getIdentityTypeAssigned(localStore, isIdentityEnabled) }
        };

        const azureCsAccountSwagger = await azureService.updateAzureCsAccountByPath({
            tenantToken,
            path: localStore.path,
            ...dataToUpdate
        });

        await waitForProvisioningState({
            resource: azureCsAccountSwagger,
            errorMessage: `Failed to update the resource ${azureCsAccountSwagger.name}`,
            call: () => azureService.getAzureCsAccountByPath({ tenantToken, path: localStore.path })
        });

        localStore.setSku(skuCode);
        localStore.setIsIdentityEnabledFlag(isIdentityEnabled);

        const sessionAzureCsAccount = rootStore.azureStore.session.csAccountStore;
        if (localStore.path === sessionAzureCsAccount?.path) {
            sessionAzureCsAccount.setSku(skuCode);
            sessionAzureCsAccount.setIsIdentityEnabledFlag(isIdentityEnabled);
            rootStore.azureStore.session.csAccountStore.setAccountTokens({ storageToken: null, armToken: null });
        }
    };

    const getIdentityTypeAssigned = (
        localStore: AzureCsAccountStore,
        isIdentityEnabled: boolean
    ): 'SystemAssigned' | 'UserAssigned' | 'SystemAssigned, UserAssigned' | 'None' => {
        if (isIdentityEnabled) {
            if (!localStore.identityType || localStore.identityType === 'None') {
                return 'SystemAssigned';
            }
            if (localStore.identityType === 'UserAssigned') {
                return 'SystemAssigned, UserAssigned';
            }
        } else {
            if (localStore.identityType === 'SystemAssigned') {
                return 'None';
            }
            if (localStore.identityType === 'SystemAssigned, UserAssigned') {
                return 'UserAssigned';
            }
        }
        return localStore.identityType;
    };

    /**
     * Updates an Azure accounts by its path.
     *
     * @param param0.ownedAzureOpenAiResourceId The new account owned azure open ai resource id to update.
     */
    const updateAzureCsAccountOpenAiResourceByPath = async ({
        ownedAzureOpenAiResourceId,
        localStore = rootStore.azureStore.session.csAccountStore
    }: HandlerParams<{ ownedAzureOpenAiResourceId: string; localStore?: AzureCsAccountStore }>) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;
        const { subscriptionId } = rootStore.azureStore.session;

        const existingSwagger = await azureService.getAzureCsAccountByPath({ tenantToken, path: localStore.path });
        const existingApiProps: { [key: string]: string } = existingSwagger.properties.apiProperties;

        // If the azure search is connected, I'll have to fetch the azure search keys again and send it with the PATCH call.
        if ('qnaAzureSearchEndpointId' in existingApiProps && existingApiProps.qnaAzureSearchEndpointId) {
            const serviceName = existingApiProps.qnaAzureSearchEndpointId.split('/')[8];
            const resourceGroupName = existingApiProps.qnaAzureSearchEndpointId.split('/')[4];
            const azureSearchKeys = await azureService.getAzureSearchResourceAdminKeys({
                tenantToken,
                resourceGroupName,
                id: subscriptionId,
                name: serviceName
            });
            existingApiProps.qnaAzureSearchEndpointKey = azureSearchKeys.primaryKey;
        }
        const dataToUpdate = <Pick<AzureCsAccountSwagger, 'properties'>>{
            properties: {
                apiProperties: {
                    ...Object.keys(existingApiProps).reduce(
                        (acc, key) => ({ ...acc, ...(existingApiProps[key] ? { [key]: existingApiProps[key] } : {}) }),
                        {}
                    ),
                    azureOpenAIAccountId: ownedAzureOpenAiResourceId
                }
            }
        };

        const azureCsAccountSwagger = await azureService.updateAzureCsAccountByPath({
            tenantToken,
            path: localStore.path,
            ...dataToUpdate
        });

        await waitForProvisioningState({
            resource: azureCsAccountSwagger,
            errorMessage: `Failed to update the resource ${azureCsAccountSwagger.name}`,
            call: () => azureService.getAzureCsAccountByPath({ tenantToken, path: localStore.path })
        });

        localStore.setOwnedAzureOpenAiResourceId(ownedAzureOpenAiResourceId);
    };

    /**
     * Updates an Azure accounts by its path.
     * if the account is the current one used in the session store,
     * it'll be updated as well.
     *
     * @param param0.ownedSearchResourceId The new account owned search resource id to update.
     */
    const updateAzureCsAccountSearchResourceByPath = async ({
        ownedSearchResourceId,
        localStore = rootStore.azureStore.session.csAccountStore
    }: HandlerParams<{ ownedSearchResourceId: string; localStore?: AzureCsAccountStore }>) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;
        const { subscriptionId } = rootStore.azureStore.session;

        const serviceName = ownedSearchResourceId.split('/')[8];
        const resourceGroupName = ownedSearchResourceId.split('/')[4];

        const qnaAzureSearchEndpointKeys = await azureService.getAzureSearchResourceAdminKeys({
            tenantToken,
            resourceGroupName,
            id: subscriptionId,
            name: serviceName
        });

        const dataToUpdate = <Pick<AzureCsAccountSwagger, 'properties'>>{
            properties: {
                apiProperties: {
                    qnaAzureSearchEndpointId: ownedSearchResourceId,
                    qnaAzureSearchEndpointKey: qnaAzureSearchEndpointKeys.primaryKey
                }
            }
        };

        const azureCsAccountSwagger = await azureService.updateAzureCsAccountByPath({
            tenantToken,
            path: localStore.path,
            ...dataToUpdate
        });

        await waitForProvisioningState({
            resource: azureCsAccountSwagger,
            errorMessage: `Failed to update the resource ${azureCsAccountSwagger.name}`,
            call: () => azureService.getAzureCsAccountByPath({ tenantToken, path: localStore.path })
        });

        localStore.setOwnedSearchResourceId(ownedSearchResourceId);

        const sessionAzureCsAccount = rootStore.azureStore.session.csAccountStore;
        if (localStore.path === sessionAzureCsAccount?.path) {
            sessionAzureCsAccount.setOwnedSearchResourceId(ownedSearchResourceId);
        }
    };

    /**
     * Creates a new azure cognitive service account.
     *
     * @param param0.id The subscription id to be used to create key under it.
     * @param param0.resourceGroupName The resource group name to be used to create key under it.
     * @param param0.keyName The name of the new key.
     * @param param0.keyProps The properties of the new key.
     */
    const createAzureCsAccount = async ({
        id,
        tracker,
        keyName,
        keyProps,
        localStore,
        resourceGroupName
    }: HandlerParams<
        Pick<AzureSubscriptionStore, 'id'> & {
            keyName: string;
            resourceGroupName: string;
            keyProps: AzureCsAccountProps;
            localStore?: ResourceStoreHouse<AzureCsAccountStore>;
        }
    >) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        // This registers the Cognitive Service resource provider for the given Azure subscription,
        // in addition to registering the required features (which is accepting the legal terms for TA for Health and TA PII)
        // all of which are required to properly create a Cognitive Services resource.
        await registerCSResourceProviderAndRequiredFeatures({ tenantToken, id });

        const azureCsAccountSwagger = await azureService.createAzureCsAccount({ tenantToken, id, resourceGroupName, keyName, keyProps });

        await waitForProvisioningState({
            resource: azureCsAccountSwagger,
            errorMessage: `Failed to create the resource ${azureCsAccountSwagger.name}`,
            call: () => azureService.getAzureCsAccountByPath({ tenantToken, path: azureCsAccountSwagger.id })
        });

        const accountStore = csAccountSwaggerToCSAccountStore(azureCsAccountSwagger);

        if (localStore) {
            localStore.add(accountStore);
        }

        if (tracker) {
            tracker.setContext(accountStore);
        }
    };

    const registerCSResourceProviderAndRequiredFeatures = async ({ id, tenantToken }: { id: string; tenantToken: string }) => {
        await registerAndWaitForRegistrationState({
            getCall: () => azureService.getAzureCsResourceProviderInfo({ tenantToken, id }),
            registerCall: () => azureService.registerAzureCsResourceProvider({ tenantToken, id }),
            registrationStateMapper: info => info.registrationState,
            errorMessage: 'Failed to register Cognitive Services resource provider'
        });

        await registerAndWaitForRegistrationState({
            getCall: () =>
                azureService.getAzureCsFeature({
                    tenantToken,
                    id,
                    featureName: LuisConstants.Azure.TAForHealthRAITermsAcceptedFeatureName
                }),
            registerCall: () =>
                azureService.registerAzureCsFeature({
                    tenantToken,
                    id,
                    featureName: LuisConstants.Azure.TAForHealthRAITermsAcceptedFeatureName
                }),
            registrationStateMapper: info => info.properties.state,
            errorMessage: `Failed to register feature: ${LuisConstants.Azure.TAForHealthRAITermsAcceptedFeatureName}`
        });

        await registerAndWaitForRegistrationState({
            getCall: () =>
                azureService.getAzureCsFeature({
                    tenantToken,
                    id,
                    featureName: LuisConstants.Azure.TAForPIIRAITermsAcceptedFeatureName
                }),
            registerCall: () =>
                azureService.registerAzureCsFeature({
                    tenantToken,
                    id,
                    featureName: LuisConstants.Azure.TAForPIIRAITermsAcceptedFeatureName
                }),
            registrationStateMapper: info => info.properties.state,
            errorMessage: `Failed to register feature: ${LuisConstants.Azure.TAForPIIRAITermsAcceptedFeatureName}`
        });
    };

    /**
     * Get the keys for the given azure resource.
     *
     * @param param0.path The azure resource path to fetch the keys for.
     * @param param0.csAccountStore The azure resource store to be updated with the keys.
     */
    const fetchAndSetCsAccountKeyByPath = async ({
        path,
        csAccountStore
    }: HandlerParams<{ path: string; csAccountStore: AzureCsAccountStore }>) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        const { canListResourceKeys } = mapUserRoleToUserActions(rootStore.serviceKey.userRole);
        const keyStrings = canListResourceKeys
            ? await azureService.getAzureCsAccountKeyStringsByPath({ tenantToken, path })
            : { key1: '', key2: '' };

        csAccountStore.setKeyStringsObject(keyStrings);
    };

    /**
     * Gets the allowed SKU list for the given azure subscription.
     *
     * @param param0.csAccountStore The azure resource to fetch the sku codes list.
     */
    const getAllowedSkusBySubscription = async ({
        localStore,
        subscriptionId
    }: HandlerParams<{ subscriptionId: string; localStore: ResourceStoreHouse<{ id: string } & AzureSkuInfo> }>) => {
        const { armToken } = rootStore.azureStore.session.userTokens;

        try {
            localStore.tracker.setState('running');
            const skuList = await azureService.getAllowedSkusBySubscription({ armToken, subscriptionId });

            localStore.setItems(
                skuList.value.map(sku => ({
                    id: `${sku.kind}_${sku.name}`,
                    ...sku,
                    locations: sku.locations.map<Region>(location => <Region>location.toLocaleLowerCase())
                }))
            );
            localStore.tracker.setState('completed');
        } catch (e) {
            localStore.tracker.setState('failed', <Error>e);
            throw e;
        }
    };

    /**
     * Refetch the current selected cs account.
     */
    const updateCurrentSelectedCsAccount = async ({}: HandlerParams<object>) => {
        const { armToken } = rootStore.azureStore.session.userTokens;
        const currentCsAccountPath = rootStore.azureStore.session.csAccountStore.path;

        const csAccountSwagger = await getAzureService().getAzureCsAccountByPath({ tenantToken: armToken, path: currentCsAccountPath });

        const updatedCsAccountStore = csAccountSwaggerToCSAccountStore(csAccountSwagger);

        rootStore.azureStore.session.setCsAccountStore(updatedCsAccountStore);
    };

    /**
     * Creates or updates a resource group.
     *
     * @param param0.id The id of the subscription to create the resource
     * groups in.
     * @param param0.location The location of the resource group to be created in.
     * @param param0.resourceGroupName The new resource group name.
     */
    const createAzureResourceGroup = async ({
        id,
        tracker,
        location,
        resourceGroupName
    }: HandlerParams<Pick<AzureSubscriptionStore, 'id'> & { resourceGroupName: string; location: Region }>) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        const azureResourceGroupSwagger = await azureService.createResourceGroup({ tenantToken, id, resourceGroupName, location });

        await waitForProvisioningState({
            resource: azureResourceGroupSwagger,
            errorMessage: `Failed to create the resource group ${azureResourceGroupSwagger.name}`,
            call: () => azureService.getAzureResourceGroup({ tenantToken, id, resourceGroupName })
        });

        const resourceGroupStore = resourceGroupSwaggerToResourceGroupStore(azureResourceGroupSwagger);
        rootStore.azureStore.resourceGroupStoreHouse.add(resourceGroupStore);

        tracker?.setContext(resourceGroupStore);
    };

    const waitForProvisioningState = async <
        T extends {
            properties: { provisioningState?: ProvisioningStateSwagger | OldProvisioningStateSwagger | AccountProvisioningStateSwagger };
        }
    >({
        call,
        resource,
        errorMessage
    }: {
        resource: T;
        errorMessage: string;
        call: () => Promise<T>;
    }) => {
        let trialNumber = 5;
        let resourceSwagger = resource;

        // Need to wait until the azure account completely created in Azure and its provisioning succeeds.
        while (
            !resourceSwagger ||
            (resourceSwagger.properties.provisioningState !== 'Succeeded' &&
                resourceSwagger.properties.provisioningState !== 'Failed' &&
                resourceSwagger.properties.provisioningState !== 'succeeded' &&
                resourceSwagger.properties.provisioningState !== 'failed')
        ) {
            resourceSwagger = await call();

            if (
                resourceSwagger.properties.provisioningState === 'Succeeded' ||
                resourceSwagger.properties.provisioningState === 'Failed' ||
                resourceSwagger.properties.provisioningState === 'succeeded' ||
                resourceSwagger.properties.provisioningState === 'failed'
            ) {
                break;
            }

            await new Promise(res => setTimeout(res, OperationDelayStrategies.exponentialDelayStrategy(null, trialNumber++)));
        }

        if (resourceSwagger.properties.provisioningState === 'Failed') {
            throw new Error(errorMessage);
        }
    };

    const registerAndWaitForRegistrationState = async <T>({
        getCall,
        registerCall,
        registrationStateMapper,
        errorMessage
    }: {
        errorMessage: string;
        getCall: () => Promise<T>;
        registerCall: () => Promise<T>;
        registrationStateMapper: (info: T) => string;
    }) => {
        let trialNumber = 5;

        let info = await getCall();
        let registrationState = registrationStateMapper(info);

        if (registrationState !== 'Registered') {
            info = await registerCall();
            registrationState = registrationStateMapper(info);
        }

        while (registrationState !== 'Registered' && trialNumber <= 10) {
            info = await getCall();
            registrationState = registrationStateMapper(info);

            if (registrationState === 'Registered') {
                break;
            }

            await new Promise(res => setTimeout(res, OperationDelayStrategies.exponentialDelayStrategy(null, trialNumber++)));
        }

        if (registrationState !== 'Registered') {
            throw new Error(errorMessage);
        }
    };

    /**
     * Loads tenant azure search service(s).
     *
     * @param param0.id The id of the subscription to get the services for.
     */
    const getAzureSearchResources = async ({
        id,
        localStoreHouse = rootStore.azureStore.searchServicesStoreHouse
    }: HandlerParams<Pick<AzureSubscriptionStore, 'id'> & { localStoreHouse?: ResourceStoreHouse<AzureSearchStore> }>) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        const services = await azureService.getAzureSearchResources({ tenantToken, id });
        localStoreHouse.setItems(services.map(service => azureSearchServiceSwaggerToAzureSearchStore(service)));
    };

    /**
     * Creates a new azure search service.
     *
     * @param param0.id The subscription id to be used to create service under it.
     * @param param0.resourceGroupName The resource group name to be used to create service under it.
     * @param param0.keyProps The properties of the new service.
     */
    const createAzureSearchResource = async ({
        id,
        tracker,
        localStoreHouse = rootStore.azureStore.searchServicesStoreHouse,
        keyProps
    }: HandlerParams<
        Pick<AzureSubscriptionStore, 'id'> & {
            keyName: string;
            keyProps: AzureSearchStore;
            localStoreHouse?: ResourceStoreHouse<AzureSearchStore>;
        }
    >) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;
        const { resourceGroupName } = rootStore.azureStore.session.csAccountStore;

        const azureSearchService = await azureService.createAzureSearchResource({ tenantToken, id, resourceGroupName, keyProps });

        await waitForProvisioningState({
            resource: azureSearchService,
            errorMessage: `Failed to create the service ${azureSearchService.name}`,
            call: () => azureService.getAzureSearchResourceByPath({ tenantToken, path: azureSearchService.id })
        });

        if (localStoreHouse) {
            localStoreHouse.add(azureSearchServiceSwaggerToAzureSearchStore(azureSearchService));
        }

        if (tracker) {
            tracker.setContext(azureSearchService);
        }
    };

    /**
     * Checks azure search service name availability.
     *
     * @param param0.id The id of the subscription to get the services for.
     * @param param0.name The name of the search service to check.
     */
    const checkAzureSearchResourceNameAvailability = async ({
        id,
        name,
        tracker
    }: HandlerParams<
        Pick<AzureSubscriptionStore, 'id'> & {
            name: string;
        }
    >) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        const nameAvailabilityResult = await azureService.checkAzureSearchResourceNameAvailability({ tenantToken, id, name });

        tracker?.setContext(nameAvailabilityResult);
    };

    /**
     * Checks for required roles on Azure CS account and Storage account.
     *
     * @param param0.id The id of the subscription containing the CS account and storage account.
     * @param param0.csAccount The Azure CS account store to check for.
     * @param param0.storageAccount The Azure storage account store to check for.
     */
    const checkAzureCsAccountAndStorageRequiredRoles = async ({
        id,
        csAccount,
        storageAccount,
        tracker
    }: HandlerParams<
        Pick<AzureSubscriptionStore, 'id'> & {
            csAccount: AzureCsAccountStore;
            storageAccount: AzureStorageAccountStore;
        }
    >) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        tracker?.setState('running');

        try {
            const [csPermissions, saPermissions] = await Promise.all([
                getNextAbleResults<'value', AzurePermissionsSwagger>(
                    (nextLink: string) =>
                        azureService.listPermissionsForCsAccount({
                            id,
                            nextLink,
                            tenantToken,
                            accountName: csAccount.name,
                            resourceGroupName: csAccount.resourceGroupName
                        }),
                    'value'
                ),
                getNextAbleResults<'value', AzurePermissionsSwagger>(
                    (nextLink: string) =>
                        azureService.listPermissionsForAzureStorageAccount({
                            id,
                            nextLink,
                            tenantToken,
                            accountName: storageAccount.name,
                            resourceGroupName: storageAccount.resourceGroupName
                        }),
                    'value'
                )
            ]);

            tracker?.setContext({
                csAccountPermissionsSatisfied: checkForAzurePermission(csPermissions, LuisConstants.Azure.AddRoleAssignmentPermission),
                storageAccountPermissionsSatisfied: checkForAzurePermission(saPermissions, LuisConstants.Azure.AddRoleAssignmentPermission)
            });

            tracker?.setState('completed');
        } catch (error) {
            tracker?.setState('failed', <Error>error);
            throw error;
        }
    };

    /**
     * Checks for required roles on Azure resource.
     *
     * @param param0.id The id of the subscription containing resource account.
     */
    const checkAndSetResourceAccountRequiredRoles = async ({
        roles,
        tracker,
        principalId,
        roleDefinition,
        resourceToCheck
    }: HandlerParams<{
        principalId?: string;
        roleDefinition?: string;
        roles: AzurePermissionsSwagger;
        resourceToCheck: AzureCsAccountStore;
    }>) => {
        const userToken = rootStore.azureStore.session.userTokens.armToken;
        const accountToken = userToken;

        try {
            const resourceAccountPermissions = await getNextAbleResults<'value', AzurePermissionsSwagger>(
                (nextLink: string) =>
                    getAzureService().listPermissionsForCsAccount({
                        nextLink,
                        tenantToken: accountToken,
                        accountName: resourceToCheck.name,
                        id: resourceToCheck.subscriptionId,
                        resourceGroupName: resourceToCheck.resourceGroupName
                    }),
                'value'
            );

            const resourceAccountPermissionsSatisfied = checkForAzurePermissions(resourceAccountPermissions, roles);

            if (!resourceAccountPermissionsSatisfied) {
                if (!principalId || !roleDefinition) {
                    throw new Error('This resource account does not have the required permissions');
                }

                try {
                    await azureService.addRoleAssignmentForAzureResource({
                        principalId,
                        tenantToken: userToken,
                        accountName: resourceToCheck.name,
                        id: resourceToCheck.subscriptionId,
                        resourceGroupName: resourceToCheck.resourceGroupName,
                        roleDefinitionId: LuisConstants.Azure.AzureRoleDefinitionId.CognitiveServicesUser
                    });

                    tracker?.setState('completed');
                } catch {
                    try {
                        await azureService.addRoleAssignmentForAzureResource({
                            principalId,
                            tenantToken: accountToken,
                            accountName: resourceToCheck.name,
                            id: resourceToCheck.subscriptionId,
                            resourceGroupName: resourceToCheck.resourceGroupName,
                            roleDefinitionId: LuisConstants.Azure.AzureRoleDefinitionId.CognitiveServicesUser
                        });
                    } catch (error) {
                        if (error.message !== 'RoleAssignmentExists: The role assignment already exists.') {
                            tracker?.setState('failed', <Error>error);
                            throw error;
                        }
                    }
                }
            }
        } catch (error) {
            throw error;
        }
    };

    const checkForAzurePermission = (permissions: AzurePermissionsSwagger[], permissionToCheck: string) =>
        permissions.some(p => {
            const isOwner = p.actions.includes('*');
            return isOwner || p.actions.includes(permissionToCheck);
        });

    /**
     * Adds required role assignments to the Azure CS account on the given Azure storage account.
     *
     * @param param0.id The id of the subscription containing the CS account and storage account.
     * @param param0.csAccount The Azure CS account store to add the role assignments for.
     * @param param0.storageAccount The Azure storage account to add the role assignment on.
     */
    const addAzureStorageRequiredRoles = async ({
        id,
        csAccount,
        storageAccount,
        tracker
    }: HandlerParams<
        Pick<AzureSubscriptionStore, 'id'> & {
            csAccount: AzureCsAccountStore;
            storageAccount: AzureStorageAccountStore;
        }
    >) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        tracker?.setState('running');

        try {
            await azureService.addRoleAssignmentForAzureStorageAccount({
                id,
                tenantToken,
                resourceGroupName: storageAccount.resourceGroupName,
                accountName: storageAccount.name,
                principalId: csAccount.principalId,
                roleDefinitionId: LuisConstants.Azure.AzureRoleDefinitionId.StorageBlobDataContributor
            });

            tracker?.setState('completed');
        } catch (error) {
            tracker?.setState('failed', <Error>error);
            throw error;
        }
    };

    return {
        getAzureTenants,
        createAzureCsAccount,
        getAzureSubscriptions,
        getAzureResourceGroups,
        getAzureSearchResources,
        createAzureResourceGroup,
        createAzureSearchResource,
        getAzureCsAccountsByPaths,
        updateAzureCsAccountByPath,
        getAllowedSkusBySubscription,
        addAzureStorageRequiredRoles,
        fetchAndSetCsAccountKeyByPath,
        updateCurrentSelectedCsAccount,
        getAzureCsAccountsBySubscriptionId,
        getAzureAmlWorkspacesBySubscriptionId,
        getAzureCsAccountsByResourceGroupName,
        checkAndSetResourceAccountRequiredRoles,
        updateAzureCsAccountOpenAiResourceByPath,
        updateAzureCsAccountSearchResourceByPath,
        checkAzureSearchResourceNameAvailability,
        checkAzureCsAccountAndStorageRequiredRoles
    };
};
